import Vue from "vue";
import User from "./models/User";
import axios from "axios";
import { analyticsController } from "./analytics/AnalyticsController";
import { CONFIGURATION_TYPE, configurationNativeTypes } from "./models/UserConfigurationTypes";
import { Notify } from "quasar";
import { introJsService } from "../onboarding/intro/IntroJsService";
import { TinyEmitter } from "tiny-emitter";
import { isInIOSApp, isInMobileApp } from "../../utils/MobileUtils";
import { BackEndService } from "../../utils/BackEndService";
import router from "src/router";
import { openSubscribePage } from "src/utils/NavigationUtils";
import { i18n, languageManager } from '../../boot/i18n';
import {levels} from './models/UserLevelsData';

class UserService extends TinyEmitter {
  private userFirstLoaded = false;
  public user = Vue.observable(new User());
  private currentlyUpdatetingConfiguration: Set<CONFIGURATION_TYPE> = new Set();

  public get isFirstLoaded(): boolean {
    return this.userFirstLoaded
  }
  public get isCheatModeEnabled(): boolean {
    return (localStorage.getItem('cheatMode') === 'true') && this.user.isAdmin;
  }

  // Axios bearer is resumed in axios boot
  public fetchUserData() {
    return BackEndService.sendGetRequest('user').then((response) => {
      this.setUserData(response.data);
      if(this.user.configurations.isLangDefaultUsed) {
        this.updateConfiguration('LANG', languageManager.currentLocaleLang)
      }
      return response.data
    });
  }
  public setUserData(userResource: any) {
    this.setUserProperties(userResource);
    introJsService.init(userResource.introductions);
    analyticsController.simplifiedAnalytics = userResource.analytics;

    if (!this.userFirstLoaded) {
      this.userFirstLoaded = true;
      this.emit('userService_firstLoaded');

      if (isInMobileApp()) {
        Vue.prototype.NotificationController.setUserInfos()

        if (this.user.configurations.onboardingConfig.hasFinishedFirstOnboarding && !Vue.prototype.NotificationController.state.isSubscribed && this.user.notificationsConsent && !Vue.prototype.NotificationController.isNotificationNerverAsked) {
          Vue.prototype.NotificationController.initialize()
        }
        if (isInIOSApp()) {
          Vue.prototype.InAppPurchases.login();
        }
      }

    }
    this.emit('userService_loaded');
  }

  /**
   * Set axios Bearer and store token in localstorage for future use
   * Init analytics
   * Update user store
   *
   * @param userData
   */
  private setUserProperties(userData: any) {
    localStorage.tokenTokatab = userData.api_token;
    axios.defaults.headers.common.Authorization = 'Bearer ' + localStorage.tokenTokatab;

    try {
      analyticsController.setAnalyticsProperties(userData);
    }
    catch (e: any) {
      console.error('Issues when initializing analytics')
      console.error(e.message);
    }
    this.user.updateDataFromJson(userData);
  }

  /* Configuration */
  public updateConfiguration(name: CONFIGURATION_TYPE, value: any) {
    if (this.currentlyUpdatetingConfiguration.has(name)) {
      return Promise.resolve();
    }
    this.currentlyUpdatetingConfiguration.add(name);
    const oldValue = this.user.configurations.getConfig(name);
    this.user.configurations.updateLocalConfigByName(name, value);

    const dbValue = (configurationNativeTypes[name] === 'json') ? JSON.stringify(value) : value;
    return BackEndService.sendPostRequest('updateConfigurationRule', { configuration_rule: name, value: dbValue })
      .catch(() => {
        this.user.configurations.updateLocalConfigByName(name, oldValue)
        Notify.create({
          type: 'negative',
          message: i18n.t('userService.errors.cannotChangeParameter') as string,
        });
      })
      .finally(() => {
        this.currentlyUpdatetingConfiguration.delete(name);
      });
  }
  public decrementFreeActions(returnHome: boolean = true) {
    if (this.user.premium && this.user.status !== 'TRIAL') {
      return
    }

    if (this.user.paymentGroupTesting === 'B') {
      if (this.user.configurations.currentDailyFreeActions > 0) {
        return this.updateConfiguration('CURRENT_DAILY_FREE_ACTIONS', this.user.configurations.currentDailyFreeActions - 1).then(() => {
          analyticsController.logBasicAnalyticsEvent('user decremented daily free actions', 'subscribtion', {
            page: router.currentRoute.meta.title,
            currentDailyFreeActions: this.user.configurations.currentDailyFreeActions,
          })
          if (this.user.configurations.currentDailyFreeActions === 0) {
            analyticsController.logBasicAnalyticsEvent('user reached daily free actions limit', 'subscribtion', {
              page: router.currentRoute.meta.title,
              currentDailyFreeActions: this.user.configurations.currentDailyFreeActions,
            })
            Notify.create({
              timeout: 4000,
              message: i18n.t('userService.freeActionsLimit.daily.message') as string,
              position: 'bottom',
              actions: [
                {
                  label: i18n.t('userService.freeActionsLimit.daily.subscribe') as string,
                  color: 'accent',
                  handler: () => {
                    openSubscribePage();
                  },
                },
              ],
            });

            if (returnHome) {
              setTimeout(() => {
                router.push('/')
              }, 2000)
            }

          }
        })
      }
    }


    if (this.user.configurations.currentWeeklyFreeActions > 0) {
      return this.updateConfiguration('CURRENT_WEEKLY_FREE_ACTIONS', this.user.configurations.currentWeeklyFreeActions - 1).then(() => {
        analyticsController.logBasicAnalyticsEvent('user decremented weekly free actions', 'subscribtion', {
          page: router.currentRoute.meta.title,
          currentWeeklyFreeActions: this.user.configurations.currentWeeklyFreeActions,
        })
        if (this.user.configurations.currentWeeklyFreeActions === 0) {
          analyticsController.logBasicAnalyticsEvent('user reached weekly free actions limit', 'subscribtion', {
            page: router.currentRoute.meta.title,
            currentWeeklyFreeActions: this.user.configurations.currentWeeklyFreeActions,
          })
          Notify.create({
            timeout: 4000,
            message: i18n.t('userService.freeActionsLimit.weekly.message') as string,
            position: 'bottom',
            actions: [
              {
                label: i18n.t('userService.freeActionsLimit.weekly.subscribe') as string,
                color: 'accent',
                handler: () => {
                  openSubscribePage();
                },
              },
            ],
          });

          if (returnHome) {
            setTimeout(() => {
              router.push('/')
            }, 2000)
          }

        }
      })
    }

  }

  /* Newsletter */
  public updateNewsletterConsent(consentValue: boolean) {
    this.user.newsletterConsent = consentValue;
    return BackEndService.sendPostRequest('updateNewsletterConsent', { consentValue });
  }
  public updateNotificationsConsent(consentValue: boolean) {
    this.user.notificationsConsent = consentValue;
    return BackEndService.sendPostRequest('updateNotificationsConsent', { consentValue });
  }

  updateLevel(level: number, refreshAllSkills: boolean = true, withNotify: boolean = true) {
    return BackEndService.sendPostRequest('user/level', { level, refreshAllSkills }).then(
      (response) => {
        this.user.level = response.content.level
        if (withNotify) {
          Notify.create({
            message: i18n.t('userService.updateLevel.notifyText', { levelName: levels.find(level => level.value === this.user.level.value)?.name }) as string,
            classes: 'teacher__notification',
            icon: undefined,
            color: 'positive',
            position: 'bottom',
            timeout: 3000,
          });
        }
        analyticsController.addAmplitudeIdentityInformation('LEVEL', this.user.level.name);
        this.emit('userService_levelUpdated', this.user.level);
      }
    );
  }

  logout() {
    this.userFirstLoaded = false;
    this.user.id = 0
    this.user = Vue.observable(new User());
    if (isInIOSApp() && isInMobileApp()) {
      // @ts-ignore
      Vue.prototype.InAppPurchases.logout();
    }
    this.emit('userService_loggedOut');
  }

  extendTrial(forceRefresh: boolean = false) {
    BackEndService.sendPostRequest('user/extend-trial').then(() => {
      if (forceRefresh) {       
        userService.fetchUserData().then();
      }
    });
  }
}

export const  userService = new UserService();
